import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Graph } from '../dashboardTypes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GraphNoDataMessage } from '../../Graphs/GraphNoDataMessage';
import { sliceGraphDataForRawDisplay } from '../../Graphs/sensor-utils';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { ApplicationActions } from '../../../utils/constants';

interface RawDataWidgetProps {
  graph: Graph | undefined;
}

const RawDataWidget = ({ graph }: RawDataWidgetProps) => {
  const [graphDataKeys, setGraphDataKeys] = useState<string[]>([]);
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const isLiveMode = dashboardViewMode === ApplicationActions.DashboardViewMode.LIVE;

  const displayRows = useMemo(() => {
    const rows = graph?.data ?? [];
    if (!isLiveMode) return rows;
    return sliceGraphDataForRawDisplay(rows);
  }, [graph?.data, isLiveMode]);

  useEffect(() => {
    if (!graph?.series?.length) {
      setGraphDataKeys([]);
      return;
    }
    const seriesKeys = graph.series.map((series) => series.dataKey).filter((key) => key !== 'time');
    setGraphDataKeys(['time', ...seriesKeys]);
  }, [graph]);

  const roundToTwo = (num: number | null | undefined) => {
    if (typeof num !== 'number' || !Number.isFinite(num)) return;
    return Math.round((num + Number.EPSILON) * 100) / 100;
  };

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [displayRows]);

  return (
    <Box
      className="widget-body"
      justifyContent="center"
      alignItems="center"
      display="flex"
      sx={{
        overflow: 'auto',
        p: 1,
      }}
    >
      {(graph?.series?.length ?? 0) > 0 ? (
        <TableContainer ref={listRef} sx={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {graphDataKeys.map((dataKey) => (
                  <TableCell key={dataKey} align="center">
                    {dataKey}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, index) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={`${index}-${row.time}`}>
                  {graphDataKeys.map((dataKey) => {
                    const value = row[dataKey];
                    return (
                      <TableCell key={dataKey} align="right">
                        {roundToTwo(value)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <GraphNoDataMessage />
      )}
    </Box>
  );
};

export default RawDataWidget;
